import { useEffect, useRef } from 'react';
import { gsap, ScrollTrigger } from '@/hooks/useGsapAnimations';

gsap.registerPlugin(ScrollTrigger);

const LearningStages = () => {
    const sectionRef = useRef<HTMLElement>(null);
    const headerRef = useRef<HTMLDivElement>(null);
    const gridRef = useRef<HTMLDivElement>(null);

    const stages = [
        {
            icon: "/LearningIcon.svg",
            title: "Empower Your Learning",
            subtitle: "TOOLS THAT HELP YOU LEARN FASTER",
            description: "Turn textbook questions and notes into instant answers, summaries, and quizzes. Scan homework, organize subjects into folders, and revise faster with everything saved.",
            badge: "For Students",
            gradient: "from-[#8E57FA] to-[#6B3FD9]"
        },
        {
            icon: "/TeachingIcon.svg",
            title: "Empower Your Teaching",
            subtitle: "TOOLS THAT WORK AS HARD AS YOU DO",
            description: "Generate quizzes, notes, and study materials from books or worksheets instantly. Manage lessons, track content, and prepare classroom resources without extra workload.",
            badge: "For Educators",
            gradient: "from-[#1a1425] to-[#1a1425]"
        },
        {
            icon: "/ResearchIcon.svg",
            title: "Refine Your Research",
            subtitle: "BUILT FOR DEEP WORK",
            description: "Scan research papers or long documents to get clear notes and quick summaries. Store and organize study material for assignments and exam preparation easily.",
            badge: "For Graduates",
            gradient: "from-[#1a1425] to-[#1a1425]"
        },

        {
            icon: "/ExpertiseIcon.svg",
            title: "Elevate Your Expertise",
            subtitle: "LEARN & UPSKILL FASTER",
            description: "Convert documents into clear notes and quick quizzes to learn new skills faster. Keep important knowledge saved in one place and searchable anytime for quick access.",
            badge: "For Professionals",
            gradient: "from-[#1a1425] to-[#1a1425]"
        }
    ];

    useEffect(() => {
        const el = sectionRef.current;
        if (!el) return;

        const ctx = gsap.context(() => {
            // Header fade in
            if (headerRef.current) {
                gsap.fromTo(
                    headerRef.current,
                    { opacity: 0, y: 30 },
                    {
                        opacity: 1,
                        y: 0,
                        duration: 0.8,
                        ease: 'power2.out',
                        scrollTrigger: {
                            trigger: headerRef.current,
                            start: 'top 85%',
                            toggleActions: 'play none none none',
                        },
                    }
                );
            }

            // Cards stagger in from bottom with slight scale
            if (gridRef.current) {
                const cards = gridRef.current.querySelectorAll('.stage-card');
                gsap.fromTo(
                    cards,
                    { opacity: 0, y: 50, scale: 0.95 },
                    {
                        opacity: 1,
                        y: 0,
                        scale: 1,
                        duration: 0.7,
                        ease: 'power2.out',
                        stagger: 0.15,
                        scrollTrigger: {
                            trigger: gridRef.current,
                            start: 'top 85%',
                            toggleActions: 'play none none none',
                        },
                    }
                );
            }
        }, el);

        return () => ctx.revert();
    }, []);

    return (
        <section ref={sectionRef} className="relative z-10 px-4 sm:px-6 lg:px-10 py-8 sm:py-12 md:py-12 lg:py-12 max-w-[1440px] mx-auto">
            {/* Section Header */}
            <div ref={headerRef} className="text-center mb-12 sm:mb-16 md:mb-20 space-y-4 sm:space-y-6" style={{ opacity: 0 }}>
                <h2
                    className="text-white"
                    style={{
                        fontFamily: "'Poppins', sans-serif",
                        fontWeight: 500,
                        fontSize: "clamp(24px, 6vw, 40px)",
                        lineHeight: "1.3",
                        letterSpacing: "0px",
                    }}
                >
                    Built for every stage of learning
                </h2>
                <p
                    className="text-[#DCCBFD] max-w-3xl mx-auto"
                    style={{
                        fontFamily: "'Roboto', sans-serif",
                        fontWeight: 400,
                        fontStyle: "Regular",
                        fontSize: "clamp(10px, 3.5vw, 16px)",
                        lineHeight: "clamp(22px, 5vw, 26px)",
                        letterSpacing: "0.5px",
                    }}
                >
                    Whether you are mastering the basics, guiding a classroom, or advancing your career, our tools adapt to your unique academic journey.
                </p>
            </div>

            {/* Cards Grid */}
            <div ref={gridRef} className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8">
                {stages.map((stage, index) => (
                    <div
                        key={index}
                        className="stage-card group rounded-3xl px-3 py-4 border flex flex-col justify-between  border-[#FFFFFF1A] backdrop-blur-sm transition-all duration-300 hover:scale-105 hover:border-[#8E57FA]/50 bg-[#1a1425]/40 hover:bg-gradient-to-b hover:from-[#8E57FA4D]/70 hover:to-[#8E57FA4D]/10 hover:shadow-[0_20px_40px_rgba(142,87,250,0.2)]"
                        style={{
                            boxShadow: '0 10px 30px rgba(0, 0, 0, 0.3)',
                            opacity: 0,
                        }}

                    >
                        {/* Icon */}
                        <div className="mb-6 sm:mb-7 md:mb-8">

                            <img src={`${stage.icon}`} alt="" />

                        </div>

                        {/* Content */}
                        <div className="space-y-2 sm:space-y-1">
                            {/* Title */}
                            <h3
                                className="text-white"
                                style={{
                                    fontFamily: "'Poppins', sans-serif",
                                    fontWeight: 500,
                                    fontSize: "clamp(18px, 4vw, 20px)",
                                    lineHeight: "30px",
                                    letterSpacing: "0px",
                                    fontStyle: "medium",
                                }}
                            >
                                {stage.title}
                            </h3>

                            {/* Subtitle */}
                            <p
                                className="text-[#8E57FA]"
                                style={{
                                    fontFamily: "'Poppins', sans-serif",
                                    fontWeight: 400,
                                    fontStyle: "Italic",
                                    fontSize: "clamp(10px, 2.5vw, 10px)",
                                    lineHeight: "21px",
                                    letterSpacing: "1.5px",

                                }}
                            >
                                {stage.subtitle}
                            </p>

                            {/* Description */}
                            <p
                                className={ "text-[#DCCBFD]"}
                                style={{
                                    fontFamily: "'Roboto', sans-serif",
                                    fontWeight: 400,
                                    fontSize: "clamp(13px, 3vw, 12px)",
                                    lineHeight: "clamp(20px, 4.5vw, 22px)",
                                    letterSpacing: "0.3px",
                                }}
                            >
                                {stage.description}
                            </p>
                        </div>

                        {/* Badge */}
                        <div className="mt-6 sm:mt-7 md:mt-8">
                            <span
                                className={`inline-block px-4 sm:px-5 py-2 sm:py-2.5 rounded-full text-white transition-all duration-300

                                    bg-[#8E57FA] hover:bg-[#7B48E8]'
                                    }`}
                                style={{
                                    fontFamily: "'Poppins', sans-serif",
                                    fontWeight: 500,
                                    fontSize: "clamp(12px, 3vw, 12px)",
                                    lineHeight: "1.5",
                                    letterSpacing: "0.3px",
                                }}
                            >
                                {stage.badge}
                            </span>
                        </div>
                    </div>
                ))}
            </div>
        </section>
    );
};

export default LearningStages;
